import { batch } from "react-redux";

import { hidePopUp } from "@/actions/modalActions";
import {
  AutoProfileConstants,
  BufferExchangesStateMapper,
} from "@/constants/autoProfileEnum";
import { ConicalType } from "@/constants/autoProfileOption";
import {
  FINAL_VOLUME,
  STARTING_VOLUME,
  STARTING_CONCENTRATION,
  FINAL_CONCENTRATION,
  VOLUME_TO_START_AT,
  EXCHANGE_VOLUME,
  CURRENT_BUFFER_VOLUME,
  EXCHANGE_SIZE,
  ESTIMATED_CURRENT_BUFFER_VOLUME,
} from "@/constants/fieldNames";
import {
  calcBufferExchangeParams,
  calcEstFinalConcentrationBufferExchange,
  calcExchangeSizeBufferExchange,
  calcFinalVolumeBufferExchange,
  calculateStepSizeInBufferExchange,
  parseNumberParam,
  validateBufferVolBufferExchange,
  validateExchangeStepSize,
} from "@/libs/autoProfileHelper";
import { objMapper } from "@/libs/objectHelper";

import {
  bulkUpdateProfileParamByName,
  removeInvalidAutoProfileParamByName,
  showExchangeVolumeModal,
  validateCurrBufferVolRefill,
} from "./autoProfileBusiness";

export const changeBufferExchangeParam = (type, value) => (dispatch) => {
  let params = {};

  switch (type) {
    case STARTING_VOLUME:
      params = dispatch(setStartingVol(value));
      break;
    case FINAL_VOLUME:
      params = dispatch(setFinalVol(value));
      break;
    case STARTING_CONCENTRATION:
      params = dispatch(setStartingConcVol(value));
      break;
    case FINAL_CONCENTRATION:
      params = dispatch(setEstFinalConcVol(value));
      break;
    case VOLUME_TO_START_AT:
      params = dispatch(setVolToStartAt(value));
      break;
    case EXCHANGE_VOLUME:
      params = dispatch(setBufferVolumeToUse(value));
      break;
    case CURRENT_BUFFER_VOLUME:
    case ESTIMATED_CURRENT_BUFFER_VOLUME:
      params = dispatch(setTotalBufferVolumeToUse(value));
      break;
    case EXCHANGE_SIZE:
      params = dispatch(setExchangeStepSize(value));
      break;

    default:
      break;
  }

  return objMapper(params, BufferExchangesStateMapper);
};

export const setStartingVol = (value) => (dispatch, getState) => {
  const { setupStore } = { ...getState() };
  const { currentProfileParams } = setupStore;
  const {
    FinalVolume,
    StartingConcentration,
    EstimatedFinalConcentration,
    ConicalVolume,
    RecoveryOfLiquid,
  } = currentProfileParams;
  const startingVol = Number(value);
  let finalVol = parseNumberParam(FinalVolume);
  let startingConc = parseNumberParam(StartingConcentration);
  let estFinalConcentration = parseNumberParam(EstimatedFinalConcentration);
  const conicalVolume = parseNumberParam(ConicalVolume) || ConicalType[50];

  const removeInvalidParams = [STARTING_VOLUME];
  let profileParam = {
    StartingVolume: startingVol,
  };

  if (!startingConc) {
    startingConc = AutoProfileConstants.DEFAULT_STARTING_CONC;
    removeInvalidParams.push(STARTING_CONCENTRATION);
    profileParam = {
      ...profileParam,
      StartingConcentration: startingConc,
    };
  }

  if (finalVol && estFinalConcentration && finalVol > startingVol) {
    finalVol = calcFinalVolumeBufferExchange(
      startingVol,
      estFinalConcentration,
      startingConc
    );
    removeInvalidParams.push(FINAL_VOLUME);
    profileParam = {
      ...profileParam,
      FinalVolume: finalVol,
    };
  }

  if (finalVol && startingConc) {
    estFinalConcentration = calcEstFinalConcentrationBufferExchange(
      startingVol,
      finalVol,
      startingConc
    );
    removeInvalidParams.push(FINAL_CONCENTRATION);
    profileParam = {
      ...profileParam,
      EstimatedFinalConcentration: estFinalConcentration,
    };
  }

  if (finalVol) {
    const calculatedBufferExchangeParams = calcBufferExchangeParams(
      startingVol,
      finalVol,
      RecoveryOfLiquid,
      conicalVolume
    );
    removeInvalidParams.concat([
      VOLUME_TO_START_AT,
      EXCHANGE_VOLUME,
      CURRENT_BUFFER_VOLUME,
      EXCHANGE_SIZE,
    ]);
    profileParam = {
      ...profileParam,
      VolumeToStartAt: calculatedBufferExchangeParams.VolumeToStartAt,
      BufferVolumeToUse: calculatedBufferExchangeParams.BufferVolumeToUse,
      TotalBufferVolumeToUse:
        calculatedBufferExchangeParams.TotalBufferVolumeToUse,
      ExchangeStepSize: calculatedBufferExchangeParams.ExchangeStepSize,
      StepSizeVolume: calculatedBufferExchangeParams.StepSizeVolume,
    };
  }

  batch(() => {
    dispatch(removeInvalidAutoProfileParamByName(removeInvalidParams));
    dispatch(bulkUpdateProfileParamByName(profileParam));
  });

  return profileParam;
};

export const setFinalVol = (value) => (dispatch, getState) => {
  const { setupStore } = { ...getState() };
  const { currentProfileParams } = setupStore;
  const {
    StartingVolume,
    StartingConcentration,
    EstimatedFinalConcentration,
    ConicalVolume,
    RecoveryOfLiquid,
    VolumeToStartAt,
    ExchangeStepSize,
    StepSizeVolume,
  } = currentProfileParams;
  const finalVol = Number(value);
  const startingVol = parseNumberParam(StartingVolume);
  const startingConc = parseNumberParam(StartingConcentration);
  let estFinalConcentration = parseNumberParam(EstimatedFinalConcentration);
  const conicalVolume = parseNumberParam(ConicalVolume) || ConicalType[50];
  const volumeToStartAt = parseNumberParam(VolumeToStartAt);
  const exchangeStepSize = parseNumberParam(ExchangeStepSize || StepSizeVolume);

  const removeInvalidParams = [FINAL_VOLUME];
  let profileParam = {
    FinalVolume: finalVol,
  };

  if (startingVol && startingConc) {
    estFinalConcentration = calcEstFinalConcentrationBufferExchange(
      startingVol,
      finalVol,
      startingConc
    );
    removeInvalidParams.push(FINAL_CONCENTRATION);
    profileParam = {
      ...profileParam,
      EstimatedFinalConcentration: estFinalConcentration,
    };
  }

  if (startingVol && finalVol > volumeToStartAt) {
    const calculatedBufferExchangeParams = calcBufferExchangeParams(
      startingVol,
      finalVol,
      RecoveryOfLiquid,
      conicalVolume
    );

    removeInvalidParams.concat([
      VOLUME_TO_START_AT,
      EXCHANGE_VOLUME,
      CURRENT_BUFFER_VOLUME,
      EXCHANGE_SIZE,
    ]);
    profileParam = {
      ...profileParam,
      VolumeToStartAt: calculatedBufferExchangeParams.VolumeToStartAt,
      BufferVolumeToUse: calculatedBufferExchangeParams.BufferVolumeToUse,
      TotalBufferVolumeToUse:
        calculatedBufferExchangeParams.TotalBufferVolumeToUse,
      ExchangeStepSize: calculatedBufferExchangeParams.ExchangeStepSize,
      StepSizeVolume: calculatedBufferExchangeParams.StepSizeVolume,
    };

    if (exchangeStepSize) {
      const validatedExchangeStepSize = Number(
        validateExchangeStepSize(
          exchangeStepSize,
          profileParam.VolumeToStartAt,
          conicalVolume
        ).toFixed(2)
      );

      profileParam = {
        ...profileParam,
        ExchangeStepSize: validatedExchangeStepSize,
        StepSizeVolume: validatedExchangeStepSize,
      };
    }
  }

  batch(() => {
    dispatch(removeInvalidAutoProfileParamByName(removeInvalidParams));
    dispatch(bulkUpdateProfileParamByName(profileParam));
  });

  return profileParam;
};

export const setStartingConcVol = (value) => (dispatch, getState) => {
  const { setupStore } = { ...getState() };
  const { currentProfileParams } = setupStore;
  const { StartingVolume, FinalVolume, EstimatedFinalConcentration } =
    currentProfileParams;

  const finalVol = parseNumberParam(FinalVolume);
  const startingVol = parseNumberParam(StartingVolume);
  const startingConc = Number(value);
  let estFinalConcentration = parseNumberParam(EstimatedFinalConcentration);

  const removeInvalidParams = [STARTING_CONCENTRATION];
  let profileParam = {
    StartingConcentration: startingConc,
  };

  if (startingVol && finalVol) {
    estFinalConcentration = calcEstFinalConcentrationBufferExchange(
      startingVol,
      finalVol,
      startingConc
    );
    removeInvalidParams.push(FINAL_CONCENTRATION);
    profileParam = {
      ...profileParam,
      EstimatedFinalConcentration: estFinalConcentration,
    };
  }

  batch(() => {
    dispatch(removeInvalidAutoProfileParamByName(removeInvalidParams));
    dispatch(bulkUpdateProfileParamByName(profileParam));
  });

  return profileParam;
};

export const setEstFinalConcVol = (value) => (dispatch, getState) => {
  const { setupStore } = { ...getState() };
  const { currentProfileParams } = setupStore;
  const {
    StartingVolume,
    FinalVolume,
    StartingConcentration,
    ConicalVolume,
    RecoveryOfLiquid,
  } = currentProfileParams;

  let finalVol = parseNumberParam(FinalVolume);
  const startingVol = parseNumberParam(StartingVolume);
  const startingConc = parseNumberParam(StartingConcentration);
  const estFinalConcentration = Number(value);
  const conicalVolume = parseNumberParam(ConicalVolume) || ConicalType[50];

  const removeInvalidParams = [FINAL_CONCENTRATION];
  let profileParam = {
    EstimatedFinalConcentration: estFinalConcentration,
  };

  if (startingVol && startingConc) {
    finalVol = calcFinalVolumeBufferExchange(
      startingVol,
      estFinalConcentration,
      startingConc
    );
    removeInvalidParams.push(FINAL_VOLUME);
    profileParam = {
      ...profileParam,
      FinalVolume: finalVol,
    };
  }

  if (startingVol) {
    const calculatedBufferExchangeParams = calcBufferExchangeParams(
      startingVol,
      finalVol,
      RecoveryOfLiquid,
      conicalVolume
    );
    removeInvalidParams.concat([
      VOLUME_TO_START_AT,
      EXCHANGE_VOLUME,
      CURRENT_BUFFER_VOLUME,
      EXCHANGE_SIZE,
    ]);
    profileParam = {
      ...profileParam,
      VolumeToStartAt: calculatedBufferExchangeParams.VolumeToStartAt,
      BufferVolumeToUse: calculatedBufferExchangeParams.BufferVolumeToUse,
      TotalBufferVolumeToUse:
        calculatedBufferExchangeParams.TotalBufferVolumeToUse,
      ExchangeStepSize: calculatedBufferExchangeParams.ExchangeStepSize,
      StepSizeVolume: calculatedBufferExchangeParams.StepSizeVolume,
    };
  }

  batch(() => {
    dispatch(removeInvalidAutoProfileParamByName(removeInvalidParams));
    dispatch(bulkUpdateProfileParamByName(profileParam));
  });

  return profileParam;
};

export const setVolToStartAt = (value) => (dispatch, getState) => {
  const { setupStore } = { ...getState() };
  const { currentProfileParams } = setupStore;
  const { BufferVolumeToUse, ExchangeStepSize, ConicalVolume } =
    currentProfileParams;

  const volumeToStartAt = Number(value);
  const bufferVolumeToUse = parseNumberParam(BufferVolumeToUse);
  let exchangeStepSize = parseNumberParam(ExchangeStepSize);
  const conicalVolume = parseNumberParam(ConicalVolume) || ConicalType[50];

  const removeInvalidParams = [VOLUME_TO_START_AT];
  let profileParam = {
    VolumeToStartAt: volumeToStartAt,
  };

  if (bufferVolumeToUse) {
    exchangeStepSize = calcExchangeSizeBufferExchange(
      bufferVolumeToUse,
      volumeToStartAt,
      conicalVolume
    );

    const validatedExchangeStepSize = Number(
      validateExchangeStepSize(
        exchangeStepSize,
        volumeToStartAt,
        conicalVolume
      ).toFixed(2)
    );

    removeInvalidParams.push([EXCHANGE_SIZE]);
    profileParam = {
      ...profileParam,
      ExchangeStepSize: validatedExchangeStepSize,
      StepSizeVolume: validatedExchangeStepSize,
    };
  }

  batch(() => {
    dispatch(removeInvalidAutoProfileParamByName(removeInvalidParams));
    dispatch(bulkUpdateProfileParamByName(profileParam));
  });

  return profileParam;
};

export const setBufferVolumeToUse = (value) => (dispatch, getState) => {
  const { setupStore } = { ...getState() };
  const { currentProfileParams } = setupStore;
  const {
    VolumeToStartAt,
    ExchangeStepSize,
    TotalBufferVolumeToUse,
    ConicalVolume,
  } = currentProfileParams;
  const volumeToStartAt = parseNumberParam(VolumeToStartAt);
  const bufferVolumeToUse = Number(value);
  const totalBufferVolumeToUse = parseNumberParam(TotalBufferVolumeToUse);
  let exchangeStepSize = parseNumberParam(ExchangeStepSize);
  const conicalVolume = parseNumberParam(ConicalVolume) || ConicalType[50];

  const bufferVolume = validateBufferVolBufferExchange(
    totalBufferVolumeToUse,
    bufferVolumeToUse
  );

  let profileParam = {};

  const removeInvalidParams = [EXCHANGE_VOLUME];
  profileParam = {
    ...profileParam,
    BufferVolumeToUse: bufferVolumeToUse,
  };

  if (volumeToStartAt && bufferVolumeToUse < exchangeStepSize) {
    exchangeStepSize = bufferVolumeToUse;

    removeInvalidParams.push([EXCHANGE_SIZE]);
    profileParam = {
      ...profileParam,
      ExchangeStepSize: exchangeStepSize,
      StepSizeVolume: exchangeStepSize,
    };
  }

  const result = Number(
    calculateStepSizeInBufferExchange(
      volumeToStartAt,
      bufferVolumeToUse,
      conicalVolume
    )
  ).toFixed(2);

  profileParam = {
    ...profileParam,
    ExchangeStepSize: result,
    StepSizeVolume: result,
  };

  const isInvalidTotalBufferVol =
    totalBufferVolumeToUse && totalBufferVolumeToUse < bufferVolume;

  batch(() => {
    dispatch(removeInvalidAutoProfileParamByName(removeInvalidParams));
    dispatch(validateCurrBufferVolRefill(isInvalidTotalBufferVol));
    dispatch(bulkUpdateProfileParamByName(profileParam));
  });

  if (isInvalidTotalBufferVol) {
    dispatch(
      showExchangeVolumeModal(
        totalBufferVolumeToUse,
        bufferVolume,
        ({ value }) => dispatch(setBufferVolumeToUse(value))
      )
    );
  }

  return profileParam;
};

export const setTotalBufferVolumeToUse = (value) => (dispatch, getState) => {
  const { setupStore } = { ...getState() };
  const { currentProfileParams } = setupStore;
  const { BufferVolumeToUse } = currentProfileParams;
  const bufferVolumeToUse = parseNumberParam(BufferVolumeToUse);
  const totalBufferVolumeToUse = Number(value);

  let profileParam = {};

  const bufferVolume = validateBufferVolBufferExchange(
    totalBufferVolumeToUse,
    bufferVolumeToUse
  );

  profileParam = {
    ...profileParam,
    TotalBufferVolumeToUse: totalBufferVolumeToUse,
  };

  const isInvalidTotalBufferVol =
    totalBufferVolumeToUse && totalBufferVolumeToUse < bufferVolume;

  batch(() => {
    dispatch(validateCurrBufferVolRefill(isInvalidTotalBufferVol));
    dispatch(bulkUpdateProfileParamByName(profileParam));
    if (isInvalidTotalBufferVol) {
      dispatch(
        showExchangeVolumeModal(
          totalBufferVolumeToUse,
          bufferVolume,
          ({ value }) => dispatch(setTotalBufferVolumeToUse(value)),
          true
        )
      );
    }
  });

  return profileParam;
};

export const setExchangeStepSize = (value) => (dispatch) => {
  const stepSize = Number(value);
  const removeInvalidParams = [EXCHANGE_SIZE];
  const profileParam = {
    ExchangeStepSize: stepSize,
    StepSizeVolume: stepSize,
  };

  batch(() => {
    dispatch(removeInvalidAutoProfileParamByName(removeInvalidParams));
    dispatch(bulkUpdateProfileParamByName(profileParam));
  });

  return profileParam;
};

export const applyDiafiltrationCalculator =
  (_volumeToStartAt, _bufferNeeded, _stepSize) => (dispatch, getState) => {
    const { setupStore } = { ...getState() };
    const { currentProfileParams } = setupStore;
    const { TotalBufferVolumeToUse } = currentProfileParams;

    const totalBufferVolumeToUse = parseNumberParam(TotalBufferVolumeToUse);
    const volumeToStartAt = Number(_volumeToStartAt);
    const bufferVolumeToUse = Number(_bufferNeeded);
    const stepSize = Number(_stepSize);

    const removeInvalidParams = [
      EXCHANGE_SIZE,
      EXCHANGE_VOLUME,
      VOLUME_TO_START_AT,
    ];
    const profileParam = {
      ExchangeStepSize: stepSize,
      StepSizeVolume: stepSize,
      VolumeToStartAt: volumeToStartAt,
      BufferVolumeToUse: bufferVolumeToUse,
    };

    const bufferVolume = validateBufferVolBufferExchange(
      totalBufferVolumeToUse,
      bufferVolumeToUse
    );

    batch(() => {
      dispatch(removeInvalidAutoProfileParamByName(removeInvalidParams));
      dispatch(bulkUpdateProfileParamByName(profileParam));
    });

    if (totalBufferVolumeToUse < bufferVolume) {
      dispatch(
        showExchangeVolumeModal(
          totalBufferVolumeToUse,
          bufferVolume,
          ({ value }) => dispatch(setBufferVolumeToUse(value))
        )
      );
    } else {
      dispatch(hidePopUp());
    }

    return profileParam;
  };
